"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import styles from './pagination.module.scss.js';
import { validateSurface } from '../../globals.js';
import TextLinkIcon from './text-link-icon.js';
import getOS from '../../utilities/check-os/index.js';
import 'react';
import useViewport from '../../utilities/use-viewport/index.js';
import NumberLink from './number-link.js';
import { calculateRange } from './utils/calculateRange.js';
import { calculateWidth } from './utils/calculateWidth.js';
import { calculateTemplateCoulmns } from './utils/calcullateTemplateCoulmns.js';
import PaginationLeftArrow from '../../icons/component-icons/pagination-left-arrow/index.js';
import PaginationRightArrow from '../../icons/component-icons/pagination-right-arrow/index.js';

/**
 * @component Pagination
 * @package Pagination
 * @document ../_docs/code/Pagination.editor.tsx
 * @document ../_docs/code/Pagination.snippet.tsx
 * @document ../_docs/code/Pagination.import.tsx
 */
const Pagination = (_a) => {
    var { kind: kindProp = 'compact', surface: surfaceProp = 'lightPrimary', viewport: viewportProp, selectedPage = 1, total, pageRange = 5, ariaLabel, 'aria-label': ariaLabelAttr, baseUrl, onSelectPage, pageAriaLabel, pageAnalytics, previous, next, 'data-testid': testId = 'page-link', width } = _a, rest = __rest(_a, ["kind", "surface", "viewport", "selectedPage", "total", "pageRange", "ariaLabel", 'aria-label', "baseUrl", "onSelectPage", "pageAriaLabel", "pageAnalytics", "previous", "next", 'data-testid', "width"]);
    const viewport = useViewport('expanded', viewportProp);
    const isMobile = viewport === 'mobile' ||
        viewport === 'mobileLarge' ||
        viewport === 'mobileSmall';
    const kind = isMobile ? 'compact' : kindProp;
    const paginationListStyles = [
        styles.paginationList,
        styles[kind],
        styles[pageRange === 7 ? 'pageRangeSeven' : 'pageRangeFive'],
    ];
    const surface = validateSurface(surfaceProp);
    const staticNumbersStyles = [
        styles.numberStaticItem,
        styles[surface.toLowerCase()],
    ];
    const prevItemStyles = [styles.textLinkIconItem, styles.left];
    const nextItemStyles = [styles.textLinkIconItem, styles.right];
    const _onClick = (e) => {
        /* istanbul ignore else -- @preserve */
        if (e.type === 'click') {
            e.currentTarget.blur();
        }
    };
    const isIos = getOS() === 'ios';
    const Element = isIos ? 'div' : 'nav';
    const isNxtSelected = selectedPage === total;
    const isPrevSelected = selectedPage === 1;
    if (isPrevSelected) {
        prevItemStyles.push(styles.inVisible);
    }
    if (isNxtSelected) {
        nextItemStyles.push(styles.inVisible);
    }
    const _selected = (page) => {
        if (onSelectPage)
            onSelectPage(page);
    };
    const ariaLabelValue = ariaLabelAttr || ariaLabel || '';
    const pageAriaString = pageAriaLabel ? pageAriaLabel(selectedPage) : '';
    let groupAriaLabel = `Pagination of ${total} pages`;
    if (kind === 'compact') {
        groupAriaLabel = `${ariaLabelValue} Pagination of ${total} pages. Page ${selectedPage} of ${total} ${pageAriaString || ''} currently selected`;
    }
    const renderCompactNumbers = () => {
        const selectedLabel = `${ariaLabelValue} Page ${selectedPage} of ${total} ${pageAriaString || ''} selected`;
        return (jsxs("li", { className: staticNumbersStyles.join(' '), children: [jsxs("span", { "aria-hidden": true, children: [selectedPage, " ", jsx("span", { className: styles.separator, children: "/" }), " ", total] }), jsx("span", { className: styles.screenreaderText, "aria-live": selectedPage ? 'assertive' : undefined, children: selectedLabel })] }));
    };
    const renderExpandedNumbers = () => {
        const { startRange, endRange } = calculateRange(selectedPage, total, pageRange);
        return Array.from({ length: total + 1 }, (v, i) => i).map((item, index) => {
            const isInRange = index >= startRange && index <= endRange && index !== 0;
            const isSelected = selectedPage === index;
            const baseUrlString = !isSelected && baseUrl && baseUrl(index) ? baseUrl(index) : undefined;
            const pageLinkAriaString = pageAriaLabel && index ? pageAriaLabel(index) : '';
            let linkAriaLabel = `Go to Page ${index} of ${total}`;
            if (isSelected) {
                linkAriaLabel = `Page ${index} of ${total}`;
            }
            const screenReaderLabel = isSelected
                ? `Page ${index} ${pageLinkAriaString || ''} selected`
                : undefined;
            return (isInRange && (jsx("li", { className: styles.numberLinkItem, children: jsx(NumberLink, Object.assign({}, (pageAnalytics && pageAnalytics(index)), { pageNumber: index, href: baseUrlString, onClick: _selected, surface: surface, "data-testid": `${testId}-${index}`, isSelected: isSelected, ariaLabel: ariaLabelValue || pageAriaLabel
                        ? `${ariaLabelValue} ${linkAriaLabel} ${pageLinkAriaString}`
                        : linkAriaLabel, screenReaderLabel: screenReaderLabel, children: index })) }, index)));
        });
    };
    if (total > 1) {
        return (jsx(Element, { className: styles.pagination, id: rest.id, tabIndex: -1, "aria-label": groupAriaLabel, onClick: _onClick, "data-testid": testId, children: jsxs("ul", { 
                // role presentation added for accessibility. To prevent from announcing the number of list items.
                role: "presentation", className: paginationListStyles.join(' '), style: {
                    width: calculateWidth(kind, width),
                    gridTemplateColumns: total < pageRange
                        ? calculateTemplateCoulmns(kind, total)
                        : undefined,
                }, children: [jsx("li", { className: prevItemStyles.join(' '), "aria-hidden": isPrevSelected, children: jsx(TextLinkIcon, Object.assign({}, previous, { onClick: () => _selected(selectedPage - 1), href: baseUrl && baseUrl(selectedPage - 1)
                                ? baseUrl(selectedPage - 1)
                                : undefined, surface: surface, iconPosition: "left", ariaLabel: `${ariaLabelAttr || ariaLabel || ''} Go to previous page.`, renderIcon: () => (jsx(PaginationLeftArrow, { size: "12px", color: `var(--vds-color-element-primary-on${surface.toLowerCase()})` })), children: previous && previous.children ? previous.children : 'Previous' })) }), kind === 'expanded'
                        ? renderExpandedNumbers()
                        : renderCompactNumbers(), jsx("li", { className: nextItemStyles.join(' '), "aria-hidden": isNxtSelected, children: jsx(TextLinkIcon, Object.assign({}, next, { onClick: () => _selected(selectedPage + 1), href: baseUrl && baseUrl(selectedPage + 1)
                                ? baseUrl(selectedPage + 1)
                                : undefined, surface: surface, iconPosition: "right", ariaLabel: `${ariaLabelAttr || ariaLabel || ''} Go to next page.`, renderIcon: () => (jsx(PaginationRightArrow, { size: "12px", color: `var(--vds-color-element-primary-on${surface.toLowerCase()})` })), children: next && next.children ? next.children : 'Next' })) })] }) }));
    }
};

export { Pagination, Pagination as default };
